import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextProviderOrder } from './ProviderOrder';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ProviderOrderDA } from '../../../../services/admin/provider.order.service';
import { ProviderInvoiceDA } from '../../../../services/admin/provider.invoice.service';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { ProviderOrder } from '../../../../models/provider.order.model';
import { ProviderInvoice } from '../../../../models/provider.invoice.model';
import { colorStatusProviderOrder } from '../../../../scripts/badge.color.script';
import { normalizeStatus, statusDocumentValue } from '../../../../libraries/status.document.library';
import { getError } from '../../../../languages/translations/response';
import { modalShow } from '../../../../utils/modal.util';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { MediaDA } from '../../../../services/admin/media.service';

import AppPurchaseViewDetail from '../component/PurchaseViewDetail';
import AppPurchaseViewRequirements from '../component/PurchaseViewRequirements';
import AppPurchaseViewAmount from '../component/PurchaseViewAmount';
import AppPurchaseViewSignatures from '../component/PurchaseViewSignatures';
import AppPurchaseViewVouchers from '../component/PurchaseViewVouchers';
import AppPreloader from '../../../../components/Preloader';
import AppModalSendEmail from '../../../../components/modal/ModalSendEmail';
import AppPurchaseViewDraft from '../component/PurchaseViewDraft';
import AppModalProviderOrderStorageCenter from '../../../../components/modal/ModalProviderOrderStorageCenter';
import DocumentNavigation from '../../../../components/element/DocumentNavigation';
import { Dropdown } from 'react-bootstrap';

export interface ProviderOrderViewPageProps {};

let errorResponse: Error, providerOrderResponse: ProviderOrder, providerInvoiceResponse: ProviderInvoice;

const ProviderOrderViewPage: React.FunctionComponent<ProviderOrderViewPageProps> = props => {
  const {asideMinimize, setAsideMinimize, setRoute, companyForUser, settingsForCompany, storageCentersForCompany} = UseOutletContextProviderOrder()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [providerOrder, setProviderOrder] = useState<ProviderOrder | null>(null)
  const setProviderOrderNormalized: Dispatch<SetStateAction<ProviderOrder | null>> = (value) => {
    setProviderOrder((prev) => {
      const next = typeof value === 'function' ? (value as (p: ProviderOrder | null) => ProviderOrder | null)(prev) : value
      if (!next) return next
      return { ...next, status: normalizeStatus(next.status) }
    })
  }
  const [vouchers, setVouchers] = useState({list: true, value: [] as {name: string, size: number, type: string, view: string, file: any}[], valid: [] as {name: boolean, file: boolean}[]})

  const loadProviderOrder = async (id_company: string, id_provider_order: string) => {
    await ProviderOrderDA.getProviderOrder(id_company, id_provider_order).then( async (response) => {
      if (response.status === 200) {
        providerOrderResponse = response.data
        providerOrderResponse.status = normalizeStatus(providerOrderResponse.status)

        let tempVouchersValue: {name: string, size: number, type: string, view: string, file: any}[] = []
        let tempVouchersValid: {name: boolean, file: boolean}[] = []

        for (let voucher of (providerOrderResponse.vouchers ?? [])) {
          const vName = voucher.original_name ?? voucher.name ?? ''
          const vType = voucher.content_type ?? 'application/pdf'
          let vView = ''

          if (voucher.id_document_inbox && companyForUser) {
            const signedUrl = await MediaDA.getDocumentSignedUrl(companyForUser.id, voucher.id_document_inbox)
            if (signedUrl) vView = signedUrl
          } else if (voucher.file) {
            vView = voucher.file
          }

          tempVouchersValue.push({
            name: vName,
            size: voucher.size ?? 0,
            type: vType,
            view: vView,
            file: vView
          })
          tempVouchersValid.push({ name: !!vName, file: !!vView })
        }

        setVouchers({list: true, value: tempVouchersValue, valid: tempVouchersValid})
        setProviderOrder(providerOrderResponse)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    }) 
  }

  const submitProviderInvoiceCreate = async () => {
    if (companyForUser && providerOrder) {
      GlobalSwal.fire({
        title: lang.labels.youWantToGenerateAProviderInvoiceFromThisProviderOrder,
        text: lang.labels.thisOptionIsUniqueAndNewInvoice,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesGenerate,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await ProviderInvoiceDA.postProviderInvoiceReference(companyForUser.id, providerOrder.id).then( (response) => {
            if (response.status === 201) {
              providerInvoiceResponse = response.data

              GlobalSwal.fire({
                title: lang.labels.successfullyCreatedInvoice,
                text: lang.labels.actionCompletedRedirectingToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              }).then( () => {
                navigate('/main/purchase/invoice/view/' + providerInvoiceResponse.id)
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const submitProviderOrderStatus = async (item: string) => {
    if (companyForUser && providerOrder) {
      GlobalSwal.fire({
        title: `${lang.labels.youWantToChangeProviderOrderStatusTo} ${statusDocumentValue(lang, item).toLowerCase()}?`,
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${lang.labels.yes}, ${statusDocumentValue(lang, item).toLowerCase()}`,
        cancelButtonText: lang.labels.noCancel,
        customClass: {confirmButton:'btn btn-primary', cancelButton:'btn btn-secondary'}
      }).then(async (result) => {
        if (result.isConfirmed) {
          await ProviderOrderDA.putProviderOrderStatus(companyForUser.id, providerOrder.id, item).then( async (response) => {
            if (response.status === 200) {
              providerOrderResponse = response.data
              setProviderOrder(providerOrderResponse)

              GlobalSwal.fire({
                title: lang.labels.successfullyUpdatedStatus,
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const executeShowModalSendEmail = () => {
    modalShow('modal-send-email')
  }

  const executeShowModalOrderStorageCenter = () => {
    modalShow('modal-provider-order-storage-center')
  }

  useEffect( () => {
    if (companyForUser && param.id) {
      loadProviderOrder(companyForUser.id, param.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.providerOrders, branch: lang.labels.viewProviderOrder}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && storageCentersForCompany && providerOrder
        ?
        <div className="d-flex flex-column flex-lg-row">
          <div className="flex-lg-row-fluid order-2 order-lg-1 mb-5 mb-lg-0 me-lg-5">
            <div className="card card-flush mb-5">
              <div className="card-header pt-3">
                <div className="card-title align-items-start flex-column">
                  <div className="d-flex align-items-center gap-3">
                    <h1 className="mb-0">{providerOrder.provider_order_number}</h1>
                    {companyForUser && param.id && <DocumentNavigation navKey="nav_provider_orders" currentId={param.id} companyId={companyForUser.id} basePath="/main/purchase/order/view/" />}
                  </div>
                  <label className="fs-6 fw-bold mt-1">
                    {lang.labels.status}:
                    <span className={`badge badge-light-${colorStatusProviderOrder(providerOrder.status)} text-uppercase fs-7 ms-2`}>{statusDocumentValue(lang, providerOrder.status)}</span>
                  </label>
                </div>
                <div className="card-toolbar">
                  <div className="d-flex justify-content-end">
                    { storageCentersForCompany.length > 0 && providerOrder.requirements.some((item) => (item.type === 'product' && item.contents.some(sub_item => (sub_item.reference ?? '').length > 0))) && !(providerOrder.stock_status === 'done' || (!providerOrder.stock_status && providerOrder.inventoried)) &&
                      <button className="btn btn-light-primary mb-1 me-2" type="button" onClick={executeShowModalOrderStorageCenter}>
                        <div className="d-flex align-items-center">
                          <span className="svg-icon svg-icon-2 ms-0">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path opacity="0.3" d="M3 13H10C10.6 13 11 13.4 11 14V21C11 21.6 10.6 22 10 22H3C2.4 22 2 21.6 2 21V14C2 13.4 2.4 13 3 13Z" fill="black"/>
                              <path d="M7 16H6C5.4 16 5 15.6 5 15V13H8V15C8 15.6 7.6 16 7 16Z" fill="black"/>
                              <path opacity="0.3" d="M14 13H21C21.6 13 22 13.4 22 14V21C22 21.6 21.6 22 21 22H14C13.4 22 13 21.6 13 21V14C13 13.4 13.4 13 14 13Z" fill="black"/>
                              <path d="M18 16H17C16.4 16 16 15.6 16 15V13H19V15C19 15.6 18.6 16 18 16Z" fill="black"/>
                              <path opacity="0.3" d="M3 2H10C10.6 2 11 2.4 11 3V10C11 10.6 10.6 11 10 11H3C2.4 11 2 10.6 2 10V3C2 2.4 2.4 2 3 2Z" fill="black"/>
                              <path d="M7 5H6C5.4 5 5 4.6 5 4V2H8V4C8 4.6 7.6 5 7 5Z" fill="black"/>
                              <path opacity="0.3" d="M14 2H21C21.6 2 22 2.4 22 3V10C22 10.6 21.6 11 21 11H14C13.4 11 13 10.6 13 10V3C13 2.4 13.4 2 14 2Z" fill="black"/>
                              <path d="M18 5H17C16.4 5 16 4.6 16 4V2H19V4C19 4.6 18.6 5 18 5Z" fill="black"/>
                            </svg>
                          </span>
                          {lang.labels.addInventory}
                        </div>
                      </button>
                    }
                    { providerOrder.status === "in-progress" &&
                      <>
                        { storageCentersForCompany.length > 0 && providerOrder.requirements.some((item) => (item.type === 'product' && item.contents.some(sub_item => (sub_item.reference ?? '').length > 0))) && !(providerOrder.stock_status === 'done' || (!providerOrder.stock_status && providerOrder.inventoried))
                          ?
                          <Dropdown>
                            <Dropdown.Toggle variant="btn btn-primary mb-1 me-2">
                              <div className="d-flex align-items-center">
                                {lang.labels.actions}
                                <span className="svg-icon svg-icon-2 me-0">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M11.4343 12.7344L7.25 8.55005C6.83579 8.13583 6.16421 8.13584 5.75 8.55005C5.33579 8.96426 5.33579 9.63583 5.75 10.05L11.2929 15.5929C11.6834 15.9835 12.3166 15.9835 12.7071 15.5929L18.25 10.05C18.6642 9.63584 18.6642 8.96426 18.25 8.55005C17.8358 8.13584 17.1642 8.13584 16.75 8.55005L12.5657 12.7344C12.2533 13.0468 11.7467 13.0468 11.4343 12.7344Z" fill="black"></path>
                                  </svg>
                                </span>
                              </div>
                            </Dropdown.Toggle>
                            <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg-light-primary w-275px p-3" align="end">
                              <Dropdown.Item bsPrefix="menu-item" onClick={() => submitProviderOrderStatus("approved")}>
                                <p className="menu-link fw-bold px-5 m-0">{lang.labels.approveProviderOrder}</p>
                              </Dropdown.Item>
                              <Dropdown.Item bsPrefix="menu-item" onClick={() => submitProviderOrderStatus("canceled")}>
                                <p className="menu-link fw-bold px-5 m-0">{lang.labels.cancelProviderOrder}</p>
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                          :
                          <>
                            <button className="btn btn-light-primary mb-1 me-2" type="button" onClick={() => submitProviderOrderStatus('approved')}>
                              <div className="d-flex align-items-center">
                                <span className="svg-icon svg-icon-2 ms-0">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path opacity="0.3" d="M10.3 14.3L11 13.6L7.70002 10.3C7.30002 9.9 6.7 9.9 6.3 10.3C5.9 10.7 5.9 11.3 6.3 11.7L10.3 15.7C9.9 15.3 9.9 14.7 10.3 14.3Z" fill="black"/>
                                    <path d="M22 12C22 17.5 17.5 22 12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2C17.5 2 22 6.5 22 12ZM11.7 15.7L17.7 9.70001C18.1 9.30001 18.1 8.69999 17.7 8.29999C17.3 7.89999 16.7 7.89999 16.3 8.29999L11 13.6L7.70001 10.3C7.30001 9.89999 6.69999 9.89999 6.29999 10.3C5.89999 10.7 5.89999 11.3 6.29999 11.7L10.3 15.7C10.5 15.9 10.8 16 11 16C11.2 16 11.5 15.9 11.7 15.7Z" fill="black"/>
                                  </svg>
                                </span>
                                {lang.labels.approve}
                              </div>
                            </button>
                            <button className="btn btn-light-primary mb-1 me-2" type="button" onClick={() => submitProviderOrderStatus("canceled")}>
                              <div className="d-flex align-items-center">
                                <span className="svg-icon svg-icon-2 ms-0">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path opacity="0.3" d="M12 10.6L14.8 7.8C15.2 7.4 15.8 7.4 16.2 7.8C16.6 8.2 16.6 8.80002 16.2 9.20002L13.4 12L12 10.6ZM10.6 12L7.8 14.8C7.4 15.2 7.4 15.8 7.8 16.2C8 16.4 8.3 16.5 8.5 16.5C8.7 16.5 8.99999 16.4 9.19999 16.2L12 13.4L10.6 12Z" fill="black"/>
                                    <path d="M22 12C22 17.5 17.5 22 12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2C17.5 2 22 6.5 22 12ZM13.4 12L16.2 9.20001C16.6 8.80001 16.6 8.19999 16.2 7.79999C15.8 7.39999 15.2 7.39999 14.8 7.79999L12 10.6L9.2 7.79999C8.8 7.39999 8.2 7.39999 7.8 7.79999C7.4 8.19999 7.4 8.80001 7.8 9.20001L10.6 12L7.8 14.8C7.4 15.2 7.4 15.8 7.8 16.2C8 16.4 8.3 16.5 8.5 16.5C8.7 16.5 9 16.4 9.2 16.2L12 13.4L14.8 16.2C15 16.4 15.3 16.5 15.5 16.5C15.7 16.5 16 16.4 16.2 16.2C16.6 15.8 16.6 15.2 16.2 14.8L13.4 12Z" fill="black"/>
                                  </svg>
                                </span>
                                {lang.labels.cancel}
                              </div>
                            </button>
                          </>
                        }
                        <Link to={"/main/purchase/order/update/" + providerOrder.id} className="btn btn-primary mb-1 me-2">
                          <div className="d-flex align-items-center">
                            <span className="svg-icon svg-icon-2 ms-0">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path opacity="0.3" d="M2 4.63158C2 3.1782 3.1782 2 4.63158 2H13.47C14.0155 2 14.278 2.66919 13.8778 3.04006L12.4556 4.35821C11.9009 4.87228 11.1726 5.15789 10.4163 5.15789H7.1579C6.05333 5.15789 5.15789 6.05333 5.15789 7.1579V16.8421C5.15789 17.9467 6.05333 18.8421 7.1579 18.8421H16.8421C17.9467 18.8421 18.8421 17.9467 18.8421 16.8421V13.7518C18.8421 12.927 19.1817 12.1387 19.7809 11.572L20.9878 10.4308C21.3703 10.0691 22 10.3403 22 10.8668V19.3684C22 20.8218 20.8218 22 19.3684 22H4.63158C3.1782 22 2 20.8218 2 19.3684V4.63158Z" fill="black"/>
                                <path d="M10.9256 11.1882C10.5351 10.7977 10.5351 10.1645 10.9256 9.77397L18.0669 2.6327C18.8479 1.85165 20.1143 1.85165 20.8953 2.6327L21.3665 3.10391C22.1476 3.88496 22.1476 5.15129 21.3665 5.93234L14.2252 13.0736C13.8347 13.4641 13.2016 13.4641 12.811 13.0736L10.9256 11.1882Z" fill="black"/>
                                <path d="M8.82343 12.0064L8.08852 14.3348C7.8655 15.0414 8.46151 15.7366 9.19388 15.6242L11.8974 15.2092C12.4642 15.1222 12.6916 14.4278 12.2861 14.0223L9.98595 11.7221C9.61452 11.3507 8.98154 11.5055 8.82343 12.0064Z" fill="black"/>
                              </svg>
                            </span>
                            {lang.labels.edit}
                          </div>
                        </Link>
                      </>
                    }
                    { providerOrder.status === "approved" &&
                      <button className="btn btn-primary mb-1 me-2" type="button" onClick={submitProviderInvoiceCreate}>
                        <div className="d-flex align-items-center">
                          <span className="svg-icon svg-icon-2 ms-0">
                            <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path opacity="0.3" d="M8.9 21L7.19999 22.6999C6.79999 23.0999 6.2 23.0999 5.8 22.6999L4.1 21H8.9ZM4 16.0999L2.3 17.8C1.9 18.2 1.9 18.7999 2.3 19.1999L4 20.9V16.0999ZM19.3 9.1999L15.8 5.6999C15.4 5.2999 14.8 5.2999 14.4 5.6999L9 11.0999V21L19.3 10.6999C19.7 10.2999 19.7 9.5999 19.3 9.1999Z" fill="black"/>
                              <path d="M21 15V20C21 20.6 20.6 21 20 21H11.8L18.8 14H20C20.6 14 21 14.4 21 15ZM10 21V4C10 3.4 9.6 3 9 3H4C3.4 3 3 3.4 3 4V21C3 21.6 3.4 22 4 22H9C9.6 22 10 21.6 10 21ZM7.5 18.5C7.5 19.1 7.1 19.5 6.5 19.5C5.9 19.5 5.5 19.1 5.5 18.5C5.5 17.9 5.9 17.5 6.5 17.5C7.1 17.5 7.5 17.9 7.5 18.5Z" fill="black"/>
                            </svg>
                          </span>
                          {lang.labels.generateProviderInvoice}
                        </div>
                      </button>
                    }
                    <button className="btn btn-primary mb-1 me-2" type="button" onClick={executeShowModalSendEmail}>
                      <div className="d-flex align-items-center">
                        <span className="svg-icon svg-icon-2 ms-0">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15.43 8.56949L10.744 15.1395C10.6422 15.282 10.5804 15.4492 10.5651 15.6236C10.5498 15.7981 10.5815 15.9734 10.657 16.1315L13.194 21.4425C13.2737 21.6097 13.3991 21.751 13.5557 21.8499C13.7123 21.9488 13.8938 22.0014 14.079 22.0015H14.117C14.3087 21.9941 14.4941 21.9307 14.6502 21.8191C14.8062 21.7075 14.9261 21.5526 14.995 21.3735L21.933 3.33649C22.0011 3.15918 22.0164 2.96594 21.977 2.78013C21.9376 2.59432 21.8452 2.4239 21.711 2.28949L15.43 8.56949Z" fill="black"/>
                            <path opacity="0.3" d="M20.664 2.06648L2.62602 9.00148C2.44768 9.07085 2.29348 9.19082 2.1824 9.34663C2.07131 9.50244 2.00818 9.68731 2.00074 9.87853C1.99331 10.0697 2.04189 10.259 2.14054 10.4229C2.23919 10.5869 2.38359 10.7185 2.55601 10.8015L7.86601 13.3365C8.02383 13.4126 8.19925 13.4448 8.37382 13.4297C8.54839 13.4145 8.71565 13.3526 8.85801 13.2505L15.43 8.56548L21.711 2.28448C21.5762 2.15096 21.4055 2.05932 21.2198 2.02064C21.034 1.98196 20.8409 1.99788 20.664 2.06648Z" fill="black"/>
                          </svg>
                        </span>
                        {lang.labels.sendByEmail}
                      </div>
                    </button>
                    <Link to={"/main/purchase/order/create/reference/" + providerOrder.id} className="btn btn-primary mb-1">
                      <div className="d-flex align-items-center">
                        <span className="svg-icon svg-icon-2 ms-0">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect opacity="0.5" x="7" y="2" width="14" height="16" rx="3" fill="black"/>
                            <rect x="3" y="6" width="14" height="16" rx="3" fill="black"/>
                          </svg>
                        </span>
                        {lang.labels.makeCopy}
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
              <AppPurchaseViewDetail purchase={"order"} record={providerOrder}></AppPurchaseViewDetail>
            </div>
            <AppPurchaseViewRequirements record={providerOrder}></AppPurchaseViewRequirements>
            <AppPurchaseViewAmount purchase={"order"} record={providerOrder}></AppPurchaseViewAmount>
            <AppPurchaseViewVouchers purchase={"order"} record={providerOrder} entity_type="provider-order" id_entity={providerOrder?.id} id_company={companyForUser?.id} showVoucherActions={true} showDeleteAction={true} onVouchersChanged={() => { if (companyForUser && param.id) loadProviderOrder(companyForUser.id, param.id) }}></AppPurchaseViewVouchers>
            <AppPurchaseViewSignatures record={providerOrder}></AppPurchaseViewSignatures>
          </div>
          <AppPurchaseViewDraft record={providerOrder} vouchers={vouchers} showGeneratedPdf={true} companyId={companyForUser?.id} pdfPath="provider-orders" documentId={providerOrder?.id} asideMinimize={asideMinimize} setAsideMinimize={setAsideMinimize}></AppPurchaseViewDraft>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalProviderOrderStorageCenter companyForUser={companyForUser} storageCentersForCompany={storageCentersForCompany} providerOrder={providerOrder} setProviderOrder={setProviderOrderNormalized}></AppModalProviderOrderStorageCenter>
      { companyForUser && providerOrder &&
        <AppModalSendEmail companyId={companyForUser.id} documentType="provider-orders" documentId={providerOrder.id} documentNumber={providerOrder.provider_order_number} recipientEmail={providerOrder.provider?.data?.email || ''} companyName={companyForUser.name} companyEmail={companyForUser.email || ''}></AppModalSendEmail>
      }
    </div>
  )
}

export default ProviderOrderViewPage;
